import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getFavorites, getAllSkins, removeFavoriteApi } from '../api';
import { SkinCard } from '../components/SkinCard';
import { PageHeader, Skeleton } from '../components/ui';
import { useApp } from '../stores/app';
import { useMemo } from 'react';

export function FavoritesPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { favorites, toggleFavoriteLocal } = useApp();

  const favQuery = useQuery({ queryKey: ['favorites'], queryFn: getFavorites });
  const skinsQuery = useQuery({
    queryKey: ['skins'],
    queryFn: getAllSkins,
    staleTime: 1000 * 60 * 60,
  });

  const items = useMemo(() => {
    const skins = skinsQuery.data?.skins ?? [];
    const favs = favQuery.data ?? [];
    const skinById = new Map(skins.map((s) => [s.uuid, s]));
    return favs
      .map((f) => ({ ...f, skin: skinById.get(f.skinUuid) }))
      .filter((it) => it.skin);
  }, [favQuery.data, skinsQuery.data]);

  const formatRel = (ts: number | null): string => {
    if (!ts) return t('favorites.neverSeen');
    const d = new Date(ts * 1000);
    const now = new Date();
    const days = Math.floor((now.getTime() - d.getTime()) / 86400000);
    const fmt = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
    const when =
      days === 0 ? fmt.format(0, 'day')
      : days < 30 ? fmt.format(-days, 'day')
      : fmt.format(-Math.floor(days / 30), 'month');
    return t('favorites.lastSeen', { when });
  };

  const toggleFav = async (uuid: string) => {
    toggleFavoriteLocal(uuid);
    try {
      await removeFavoriteApi(uuid);
    } finally {
      void qc.invalidateQueries({ queryKey: ['favorites'] });
    }
  };

  return (
    <div className="pt-6">
      <PageHeader title={t('favorites.title')} />

      {favQuery.isLoading || skinsQuery.isLoading ? (
        <div className="grid grid-cols-1 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[16/9]" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="mt-10 text-center text-valo-muted">
          <div className="mx-auto mb-3 h-12 w-12 grid place-items-center rounded-full border border-valo-navy-border">
            <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-valo-muted" strokeWidth="1.5">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </div>
          <p>{t('favorites.empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {items.map((it, i) => (
            <div key={it.skinUuid} className="relative">
              <SkinCard
                skin={it.skin!}
                isFavorite={favorites.has(it.skinUuid)}
                onToggleFav={toggleFav}
                delayMs={i * 60}
              />
              <div className="px-1 pt-1 text-[11px] text-valo-muted font-mono">
                {formatRel(it.lastAppearedAt)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
